import Box from "@mui/material/Box";
import List from "@mui/material/List";
import { useEffect, useState } from "react";
import UppercaseButton from "../../components/UppercaseButton/UppercaseButton";
import CompactListItem from "../../components/CompactListItem/CompactListItem";
import Searchbar from "../../components/Searchbar/Searchbar";
import Paper from "../../components/Paper/Paper";
import {
  postRequest,
  getRequest,
  SUB_ROLES_OVERVIEW_ENDPOINT,
  SUB_ROLES_ENDPOINT,
  putRequest,
  deleteRequest,
} from "../../ApiService";
import {
  ERROR_MESSAGE,
  ERROR_TITLE,
  SUB_ROLE_CREATE,
  SUB_ROLE_CREATE_ERROR,
  SUB_ROLE_SEARCH,
  SUB_ROLE_UPDATE_ERROR,
} from "../../utility/TextLabels";
import SubRoleItem from "./SubRoleItem/SubRoleItem";
import SubRoleDialog from "./SubRoleDialog/SubRoleDialog";
import { SubRoleOverview } from "./Models/SubRoleOverview";
import { SubRole } from "./Models/SubRole";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";

const SubRolesPage = () => {
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [subRoleOverview, setSubRoleOverview] = useState<SubRoleOverview[]>([]);
  const [isErrorMessageOpen, setIsErrorMessageOpen] = useState(false);
  const [errorMessageText, setErrorMessageText] = useState("");

  useEffect(() => {
    getSubRoleOverview();
  }, []);

  const getSubRoleOverview = async (searchValue?: string) => {
    try {
      await getRequest(SUB_ROLES_OVERVIEW_ENDPOINT, searchValue ?? "").then(
        (fetchedSubRoles: SubRoleOverview[]) => {
          setSubRoleOverview(fetchedSubRoles);
        }
      );
    } catch (error) {
      setErrorMessageText(ERROR_MESSAGE);
      setIsErrorMessageOpen(true);
    }
  };

  const updateSubRole = async (subRole: SubRole, subRoleId: string) => {
    try {
      await putRequest(SUB_ROLES_ENDPOINT, subRole, subRoleId);
    } catch (error) {
      setErrorMessageText(SUB_ROLE_UPDATE_ERROR);
      setIsErrorMessageOpen(true);
    }
    await getSubRoleOverview();
  };

  const deleteSubRole = async (id: string) => {
    try {
      await deleteRequest(SUB_ROLES_ENDPOINT, id);
    } catch (error) {
      setErrorMessageText(ERROR_MESSAGE);
      setIsErrorMessageOpen(true);
    }
    await getSubRoleOverview();
  };

  const createSubRole = async (subRole: SubRole) => {
    try {
      await postRequest(SUB_ROLES_ENDPOINT, subRole);
    } catch (error) {
      setErrorMessageText(SUB_ROLE_CREATE_ERROR);
      setIsErrorMessageOpen(true);
    }
  };

  const createRoleAndUpdateOverview = async (subRole: SubRole) => {
    await createSubRole(subRole);
    await getSubRoleOverview();
  };

  return (
    <>
      <ErrorMessage
        isOpen={isErrorMessageOpen}
        primaryText={ERROR_TITLE}
        additionalText={errorMessageText}
        onClose={() => setIsErrorMessageOpen(false)}
      ></ErrorMessage>

      <Paper>
        <>
          <Box display="flex" justifyContent="space-between">
            <Searchbar
              placeholder={SUB_ROLE_SEARCH}
              onChange={getSubRoleOverview}
            />
            <UppercaseButton
              color="primary"
              onClick={() => setIsRoleDialogOpen(true)}
            >
              {SUB_ROLE_CREATE}
            </UppercaseButton>
            <SubRoleDialog
              title={SUB_ROLE_CREATE}
              isOpen={isRoleDialogOpen}
              onClose={() => setIsRoleDialogOpen(false)}
              onSubmit={createRoleAndUpdateOverview}
            />
          </Box>
          <List>
            {subRoleOverview.map((role, index) => (
              <CompactListItem
                key={index}
                start={index === 0}
                end={index === subRoleOverview.length - 1}
              >
                <SubRoleItem
                  id={role.id}
                  name={role.name}
                  onUpdateSubRole={(updatedSubRole) =>
                    updateSubRole(updatedSubRole, role.id)
                  }
                  onDeleteSubRole={() => deleteSubRole(role.id)}
                />
              </CompactListItem>
            ))}
          </List>
        </>
      </Paper>
    </>
  );
};

export default SubRolesPage;
